import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useStudioStore } from '@/store/useStudioStore';
import {
  normalizeLegacyPlatformUrl,
  normalizeLegacyPlatformUrlWithTrailingSlash,
} from '@/lib/platformDomain';

const BANNER_DURATION_MS = 6000;

export const StudioPublishBanner = () => {
  const { isPublishSuccessModalOpen, lastPublishedUrl } = useStudioStore();
  const [isVisible, setIsVisible] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  const normalizedUrl = lastPublishedUrl
    ? normalizeLegacyPlatformUrl(
        lastPublishedUrl.replace(/^(https?:\/\/)+/, 'https://').replace(/([^:])\/\/+/g, '$1/'),
      )
    : '';
  const href = normalizedUrl ? normalizeLegacyPlatformUrlWithTrailingSlash(normalizedUrl) : '';
  const displayUrl = href.replace(/^https?:\/\//i, '').replace(/\/$/, '');

  useEffect(() => {
    if (!isPublishSuccessModalOpen || !displayUrl) return;

    const revealFrame = window.requestAnimationFrame(() => {
      setIsVisible(true);
    });
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      setIsVisible(false);
      hideTimerRef.current = null;
    }, BANNER_DURATION_MS);

    return () => window.cancelAnimationFrame(revealFrame);
  }, [displayUrl, isPublishSuccessModalOpen]);

  useEffect(
    () => () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  if (!isVisible || !displayUrl) return null;

  return (
    <div
      className="fixed top-0 right-0 left-0 z-[100] flex min-h-[48px] items-center justify-center gap-3 bg-[#059669] px-6 py-[13px] text-[14px] font-semibold text-white shadow-[0_2px_10px_rgba(5,150,105,0.18)]"
      role="status"
      aria-live="polite"
    >
      <Icon icon="fa6-solid:circle-check" className="shrink-0 text-[14px]" />
      <span className="shrink-0">Your page is live!</span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="max-w-[min(65vw,720px)] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-white underline underline-offset-2"
      >
        {displayUrl}
      </a>
      <button
        type="button"
        aria-label="Dismiss publish success"
        title="Dismiss"
        onClick={() => {
          setIsVisible(false);
          if (hideTimerRef.current !== null) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
          }
        }}
        className="ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[18px] leading-none text-white/75 transition-colors hover:bg-white/10 hover:text-white"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
};
